import { makeStyles } from '@mui/styles';
import Panel from 'apollo-react/components/Panel';
import qs from 'query-string';
import React, { useEffect, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';

import Details from './Details';
import DistributionModal from './DistributionModal';
import Header from './Header';
import RepositoryTree from './RepositoryTree';
import { clearErrors, handleErrors } from '../../actions/ErrorActions';
import {
  clearRepositoryConfiguration,
  setRepositoryData,
} from '../../actions/RepositoryConfigurationActions';
import { clearWarnings } from '../../actions/WarningActions';
import { getDocumentRepositoryConfig } from '../../api/documentRepositoryConfiguration';
import { getSearchTreeViewData } from '../../api/searchtreeviewdata';
import Loader from '../../components/CustomLoader';
import EditorErrorPage from '../../components/EditorErrorPage';
import Layout from '../../components/Layout';
import PanelWrapper from '../../components/PanelWrapper';
import UnsavedChangesPrompt from '../../components/UnsavedChangesPrompt';
import { PILGRIM_TOP_MENU_HEIGHT } from '../../constants/SizeConstants';
import { hasFetchError } from '../../form-designer/selectors/common.selectors';
import { setFetchErrorProp } from '../../form-designer/utils/actions/form-manager.actions.utils';
import SavedSearchDesigner from '../../saved-search-designer';
import {
  clearSavedSearch,
  loadEntityFields,
  loadSavedSearches,
} from '../../saved-search-designer/actions/editor.actions';
import {
  getRepositoryProp,
  hasRepositoryUnsavedChanges,
} from '../../selectors/RepositoryConfigurationSelectors';
import {
  getExistingRepositoryConfigData,
  getNewRepositoryConfigData,
} from '../../utils/repositoryConfigurationUtils';
import { preprocessTreeViewData, treeViewDataToOptions } from '../../utils/savedSearchUtils';

const useStyles = makeStyles({
  loadingContainer: {
    position: 'absolute',
    width: '95%',
    height: '80%',
  },
  rightPanel: {
    width: '100%',
    overflowX: 'auto',
  },
  panelGroup: {
    display: 'flex',
    overflow: 'auto',
    height: '100%',
    padding: 0,
    '& > div:nth-of-type(1) > div': {
      height: '100%',
      overflow: 'auto',
    },
    '& > div:nth-of-type(2) > div': {
      height: '100%',
    },
  },
  configurationContainer: {
    flexDirection: 'column',
    display: 'flex',
    margin: '0 15px',
    height: `calc(100vh - ${PILGRIM_TOP_MENU_HEIGHT}px - 81px)`,
  },
});

const RepositoryConfiguration = ({ location }) => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const isFetchError = useSelector(hasFetchError);
  const hasUnsavedChanges = useSelector(hasRepositoryUnsavedChanges);
  const repositoryName = useSelector((state) => getRepositoryProp(state, { propName: 'name' }));

  const [isLoading, setIsLoading] = useState(false);

  const { repositoryId, isNew } = qs.parse(location.search) || {};

  useEffect(() => {
    document.title =
      (repositoryName && `Document Repository Configuration / ${repositoryName}`) ||
      'Document Repository Configuration';
  }, [repositoryName]);

  useEffect(() => {
    const fetchRepositoryData = async () => {
      setIsLoading(true);

      const [repositoryDataResponse, searchTreeViewDataResponse] = await Promise.all([
        !isNew && getDocumentRepositoryConfig({ repositoryId }),
        getSearchTreeViewData('SmartDocumentList', true),
      ]);

      if (repositoryDataResponse?.hasError || searchTreeViewDataResponse?.hasError) {
        const savedSearchResponseErrors = setFetchErrorProp(repositoryDataResponse.errors);
        const searchTreeViewDataResponseErrors = setFetchErrorProp(
          searchTreeViewDataResponse.errors
        );

        repositoryDataResponse?.hasError && dispatch(handleErrors(savedSearchResponseErrors));
        searchTreeViewDataResponse?.hasError &&
          dispatch(handleErrors(searchTreeViewDataResponseErrors));
        return;
      }

      const { savedSearches, repositoryDetails, selectedSavedSearchId } = isNew
        ? getNewRepositoryConfigData()
        : getExistingRepositoryConfigData(repositoryDataResponse.RepositoryDetails, repositoryId);

      const searchTreeViewData = searchTreeViewDataResponse?.treeViewData;

      delete repositoryDetails.nodes;

      const entityTreeFields = preprocessTreeViewData(
        searchTreeViewData ? [searchTreeViewData] : []
      );
      const entityFields = searchTreeViewData ? treeViewDataToOptions(searchTreeViewData) : [];

      batch(() => {
        dispatch(setRepositoryData(repositoryDetails));
        dispatch(loadEntityFields({ entityFields, entityTreeFields }));
        dispatch(
          loadSavedSearches({ savedSearches, selectedSavedSearchId, isDocRepository: true })
        );
      });

      setIsLoading(false);
    };

    fetchRepositoryData();

    return () => {
      dispatch(clearSavedSearch());
      dispatch(clearRepositoryConfiguration());
      dispatch(clearErrors());
      dispatch(clearWarnings());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout>
      {isFetchError ? (
        <EditorErrorPage />
      ) : isLoading ? (
        <div className={classes.loadingContainer}>
          <Loader />
        </div>
      ) : (
        <>
          <Header />
          <div className={classes.configurationContainer}>
            <div>
              <Details />
            </div>
            <PanelWrapper classes={classes}>
              <Panel width="30%" minWidth="25%" maxWidth="50%" resizable hideButton>
                <RepositoryTree />
              </Panel>
              <div className={classes.rightPanel}>
                <SavedSearchDesigner
                  showDistribution={false}
                  sortFullWidth
                  showUnsavedPrompt={false}
                />
              </div>
            </PanelWrapper>
          </div>
        </>
      )}
      <DistributionModal />
      <UnsavedChangesPrompt hasUnsavedChanges={hasUnsavedChanges} />
    </Layout>
  );
};

export default RepositoryConfiguration;
